<template>
    <FormSection :application-id="contact.application" :showBackground="false">
        <template #content>
            <div class="d-flex mx-lg-n4 py-2">
                <div v-if="!isLoading">
                    <section class="col-12 offset-lg-1 col-lg-10 row g-0">
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="auth-contact-first-name">
                                First Name
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                name="auth-contact-first-name"
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter First Name"
                                v-model="contact.firstName"
                                :disabled="!isEditable"
                                @blur="v$.contact.firstName.$touch"
                                :class="`${!v$.contact.firstName.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.contact.firstName.$error" class="field-invalid"
                                >First Name is required.</span
                            >
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="auth-contact-last-name">
                                Last Name
                                <span class="text-danger">*</span>
                            </label>
                            <input
                                name="auth-contact-last-name"
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Last Name"
                                v-model="contact.lastName"
                                :disabled="!isEditable"
                                @blur="v$.contact.lastName.$touch"
                                :class="`${!v$.contact.lastName.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.contact.lastName.$error" class="field-invalid">Last Name is required.</span>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="date-of-birth" id="Referral_Date_Of_Birth_Label"> Date of Birth </label>
                            <DatePicker :getDate="getDate">
                                <div class="input-group date datepickerInput">
                                    <input
                                        name="dateOfBirth"
                                        id="Referral_Date_Of_Birth"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(contact.dateOfBirth)"
                                        :on-change="(e: any) => getDate(e.target.value, e.target.name)"
                                        autocomplete="off"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                            </DatePicker>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="auth-contact-role"> Role </label>
                            <TagsInput
                                :data="contactRolesEnum"
                                :tags="selectedContactRoles"
                                placeholder="Start Typing..."
                                :open-on-focus="true"
                                :disabled="!isEditable"
                                v-on:updateTags="selectedContactRolesUpdated"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="auth-contact-relationship">
                                Relationship
                                <span class="text-danger">*</span>
                            </label>
                            <select
                                class="form-select"
                                name="relationship"
                                v-model="contact.relationship"
                                @change="handleRelationshipSelection()"
                                :disabled="!isEditable"
                                @blur="v$.contact.relationship.$touch"
                                :class="`${!v$.contact.relationship.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="null">Select Relationship</option>
                                <option
                                    v-for="relationship of relationshipsEnum"
                                    :key="relationship.id"
                                    :value="relationship.value"
                                >
                                    {{ relationship.name }}
                                </option>
                            </select>
                            <span v-if="v$.contact.relationship.$error" class="field-invalid"
                                >Relationship is required.</span
                            >
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label id="gender_Label" for="gender"> Gender </label>
                            <select id="gender" class="form-select" name="gender" v-model="contact.gender">
                                <option :value="null">Select Gender</option>
                                <option v-for="value of Genders" :value="value.value" :key="value.name">
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-4" v-if="showRelationshipDescription">
                            <label for="auth-contact-rel-desc"> Relationship Description </label>
                            <input
                                name="auth-contact-rel-desc"
                                class="form-control"
                                type="text"
                                placeholder="Enter Relationship Description"
                                v-model="contact.otherRelationshipDescription"
                                :disabled="!isEditable"
                            />
                        </div>
                        <div class="w-100"></div>
                        <div class="col-12 form-group d-flex">
                            <div class="form-check custom-checkbox form-check-inline mt-2">
                                <input
                                    type="checkbox"
                                    :id="`same-patient-addr-${contact.contactOrder}`"
                                    class="form-check-input au-target"
                                    :disabled="!isEditable"
                                    v-model="addrSameAsPatientAddr"
                                />
                                <label class="form-check-label" :for="`same-patient-addr-${contact.contactOrder}`">
                                    <span class="me-1">Same as Patient Primary Address</span>
                                </label>
                            </div>
                        </div>
                    </section>
                    <section class="col-12 offset-lg-1 col-lg-10 row g-0">
                        <div class="col-12 col-lg-8">
                            <AddressForm
                                :address="contact.address"
                                :updateAddress="updateAddress"
                                :disable-inputs="addrSameAsPatientAddr || !isEditable"
                            ></AddressForm>
                        </div>
                    </section>
                    <section class="col-12 offset-lg-1 col-lg-10 row g-0">
                        <div class="form-group col-12 col-lg-5 pe-lg-2">
                            <label for="auth-contact-primary-number"> Primary Phone </label>
                            <PhoneNumber
                                name="primaryPhoneNumber"
                                :enable-country="true"
                                :phone-type-options="phoneNumberOptions"
                                v-model:value="contact.phoneNumbers[primaryNumberIndex(contact.phoneNumbers)].number"
                                v-model:extension="
                                    contact.phoneNumbers[primaryNumberIndex(contact.phoneNumbers)].extension
                                "
                                v-model:type="primaryType"
                                v-model:country="contact.phoneNumbers[primaryNumberIndex(contact.phoneNumbers)].country"
                                :disabled="!isEditable"
                                v-number-only
                            >
                            </PhoneNumber>
                        </div>
                        <div class="form-group col-12 col-lg-5 pe-lg-2">
                            <label for="auth-contact-alternate-number"> Alternate Phone </label>
                            <PhoneNumber
                                name="alternatePhoneNumber"
                                :enable-country="true"
                                :phone-type-options="phoneNumberOptions"
                                v-model:value="
                                    contact.phoneNumbers[alternativeNumberIndex(contact.phoneNumbers)].number
                                "
                                v-model:extension="
                                    contact.phoneNumbers[alternativeNumberIndex(contact.phoneNumbers)].extension
                                "
                                v-model:type="secondaryType"
                                v-model:country="
                                    contact.phoneNumbers[alternativeNumberIndex(contact.phoneNumbers)].country
                                "
                                :disabled="!isEditable"
                                v-number-only
                            >
                            </PhoneNumber>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label
                                for="auth-contact-email"
                                v-tooltip="{
                                    title: contact.hasFamilyPortalAccess
                                        ? 'Email address is required to grant access to Family Portal.'
                                        : ''
                                }"
                            >
                                Email
                            </label>
                            <span v-if="contact.hasFamilyPortalAccess" class="text-danger">*</span>
                            <input
                                name="auth-contact-email"
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Email"
                                v-model="contact.email"
                                :disabled="!isEditable"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="referral-race"> Race </label>
                            <TagsInput
                                placeholder="Select Race"
                                aria-autocomplete="off"
                                :open-on-focus="true"
                                :data="Races"
                                :tags="selectedRaces"
                                v-on:updateTags="updateSelectedRaces"
                                :disabled="isRacesDeclined"
                            />
                            <div class="form-check custom-checkbox pt-1 ms-1">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="decline-race"
                                    v-model="isRacesDeclined"
                                />
                                <label class="form-check-label" for="decline-race">Declines to Specify</label>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="referral-ethnicity"> Ethnicity </label>
                            <select
                                class="form-select"
                                name="ethnicityPalliative"
                                v-model="contact.ethnicityOnly"
                                :disabled="isEthnicityDeclined"
                            >
                                <option :value="0">Select Ethnicity</option>
                                <option v-for="[key, value] of Ethnicity" :value="value.value" :key="key">
                                    {{ value.name }}
                                </option>
                            </select>
                            <div class="form-check custom-checkbox pt-1 ms-1">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="decline-ethnicity"
                                    v-model="isEthnicityDeclined"
                                />
                                <label class="form-check-label" for="decline-ethnicity">Declines to Specify</label>
                            </div>
                        </div>

                        <div class="col-12 form-group d-flex flex-wrap">
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    :id="`contact-emergency-${contact.contactOrder}`"
                                    class="form-check-input au-target"
                                    v-model="contact.isEmergencyContact"
                                    :disabled="!isEditable"
                                />
                                <label class="form-check-label" :for="`contact-emergency-${contact.contactOrder}`">
                                    <span class="me-1"> Emergency Contact </span>
                                </label>
                            </div>
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    :id="`contact-primary-bereaved-${contact.contactOrder}`"
                                    class="form-check-input au-target"
                                    v-model="contact.isBereavedContact"
                                    :disabled="!isEditable"
                                />
                                <label
                                    class="form-check-label"
                                    :for="`contact-primary-bereaved-${contact.contactOrder}`"
                                >
                                    <span class="me-1"> Bereavement Contact </span>
                                </label>
                            </div>
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    :id="`contact-family-portal-${contact.contactOrder}`"
                                    class="form-check-input"
                                    v-model="contact.hasFamilyPortalAccess"
                                    :disabled="!isEditable"
                                />
                                <label class="form-check-label" :for="`contact-family-portal-${contact.contactOrder}`">
                                    <span class="me-1"> Grant Family Portal Access </span>
                                </label>
                            </div>
                        </div>
                    </section>
                </div>
                <div class="p-5 w-100 d-flex flex-column text-center text-primary" v-if="isLoading">
                    <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                    <div class="big-text mt-1">
                        <b>Save Contact In Progress...</b>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./AuthorizedContactsForm.ts"></script>
